import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import NavBar from "@/components/NavBar";
import AlphaFeed from "@/components/dashboard/AlphaFeed";
import ScriptGenerator from "@/components/dashboard/ScriptGenerator";
import RecipeVault from "@/components/dashboard/RecipeVault";
import GrowthPlaybook from "@/components/dashboard/GrowthPlaybook";
import CustomNicheGenerator from "@/components/dashboard/CustomNicheGenerator";
import SampleVideosGallery from "@/components/SampleVideosGallery";
import AssemblyLineWalkthrough from "@/components/AssemblyLineWalkthrough";
import DeploymentHub from "@/components/dashboard/DeploymentHub";
import Leaderboard from "@/components/dashboard/Leaderboard";
import PromptLab from "@/components/dashboard/PromptLab";
import FeedbackLounge from "@/components/dashboard/FeedbackLounge";
import LifetimeUpgrade from "@/components/dashboard/LifetimeUpgrade";
import BillingControls from "@/components/dashboard/BillingControls";
import AccountSettings from "@/components/dashboard/AccountSettings";
import DashboardNav from "@/components/dashboard/DashboardNav";
import { Crown, Trophy, Users } from "@phosphor-icons/react";

export default function Dashboard() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);

    useEffect(() => {
        api.get("/dashboard/overview").then((r) => setOverview(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally run only on mount

    // Tier label used across the welcome strip + sticky nav. Lifetime members get "Diamond".
    const tierLabel = user?.lifetime_member ? "Diamond / Lifetime" : (overview?.tier_label ?? "Founder");
    const accessLabel = user?.lifetime_member ? "DIAMOND ACCESS" : "FOUNDER ACCESS";

    return (
        <div className="min-h-screen bg-background">
            <NavBar variant="dashboard" />

            <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 py-8">
                {/* Welcome Header */}
                <div data-testid="dashboard-welcome" className="mb-8 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-8">
                        <p className="label-mono text-electric-blue mb-2">CONTROL ROOM // {accessLabel}</p>
                        <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">
                            Welcome, <span className="text-electric-blue">{user?.display_name}</span>.
                        </h1>
                        <p className="text-muted-foreground mt-2">
                            Your Lab is online. The assembly line awaits.
                        </p>
                    </div>
                    <div className="md:col-span-4 grid grid-cols-3 gap-px bg-white/10">
                        <StatCell
                            icon={Trophy}
                            label="YOUR RANK"
                            value={`#${overview?.user_rank ?? "—"}`}
                            testid="stat-rank"
                        />
                        <StatCell
                            icon={Users}
                            label="ACTIVE OPS"
                            value={overview?.total_active_members ?? "—"}
                            testid="stat-active"
                        />
                        <StatCell
                            icon={Crown}
                            label="TIER"
                            value={user?.lifetime_member ? "Diamond" : (overview?.tier_label ?? "Founder")}
                            testid="stat-tier"
                            highlight={user?.lifetime_member}
                        />
                    </div>
                </div>

                {/* Sticky in-page nav: jump-to any major section */}
                <DashboardNav tierBadge={tierLabel.toUpperCase()} />

                {/* Bento Grid Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Top: Alpha Feed (full width) */}
                    <div id="alpha-feed" className="lg:col-span-12 scroll-mt-32">
                        <AlphaFeed />
                    </div>

                    {/* Lifetime Upgrade — only renders for non-lifetime members */}
                    <div className="lg:col-span-12">
                        <LifetimeUpgrade />
                    </div>

                    {/* Billing Controls / Win-back banner */}
                    <div id="billing" className="lg:col-span-12 scroll-mt-32">
                        <BillingControls />
                    </div>

                    {/* Row 2: Script Gen (8) + Leaderboard (4) */}
                    <div id="script-engine" className="lg:col-span-8 scroll-mt-32">
                        <ScriptGenerator />
                    </div>
                    <div id="leaderboard" className="lg:col-span-4 scroll-mt-32">
                        <Leaderboard />
                    </div>

                    {/* Row 3: Recipe Vault (full) */}
                    <div id="recipe-vault" className="lg:col-span-12 scroll-mt-32">
                        <RecipeVault />
                    </div>

                    {/* Row 3.5: Growth Playbook (full) */}
                    <div id="growth-playbook" className="lg:col-span-12 scroll-mt-32">
                        <GrowthPlaybook />
                    </div>

                    {/* Row 3.6: Deployment Hub (full) */}
                    <div id="deployment" className="lg:col-span-12 scroll-mt-32">
                        <DeploymentHub />
                    </div>

                    {/* Row 3.7: Custom Niche Generator (full) — AI-build a personalized Recipe */}
                    <div id="niche-generator" className="lg:col-span-12 scroll-mt-32">
                        <CustomNicheGenerator />
                    </div>

                    {/* Row 3.8: Sample Videos Gallery — real outputs from the Lab assembly line */}
                    <div id="sample-gallery" className="lg:col-span-12 -mx-4 md:-mx-8 lg:-mx-12 scroll-mt-32">
                        <SampleVideosGallery variant="dashboard" />
                    </div>

                    {/* Row 3.9: Assembly Line walkthrough — start-to-finish demo */}
                    <div id="walkthrough" className="lg:col-span-12 -mx-4 md:-mx-8 lg:-mx-12 scroll-mt-32">
                        <AssemblyLineWalkthrough variant="dashboard" />
                    </div>

                    {/* Row 4: Prompt Lab (6) + Feedback (6) */}
                    <div id="prompt-lab" className="lg:col-span-6 scroll-mt-32">
                        <PromptLab />
                    </div>
                    <div id="feedback" className="lg:col-span-6 scroll-mt-32">
                        <FeedbackLounge />
                    </div>

                    {/* Row 5: Account Settings — full width at the bottom */}
                    <div id="account" className="lg:col-span-12 scroll-mt-32">
                        <AccountSettings />
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatCell({ icon: Icon, label, value, testid, highlight }) {
    return (
        <div data-testid={testid} className={`bg-[#0A0A0A] p-4 flex flex-col gap-1 ${highlight ? "border border-electric-blue/40" : ""}`}>
            <div className="flex items-center gap-1.5 text-electric-blue">
                <Icon size={12} weight={highlight ? "fill" : "bold"} />
                <span className="label-mono text-[10px]">{label}</span>
            </div>
            <span className={`font-heading font-black text-xl tracking-tight ${highlight ? "text-electric-blue neon-text" : ""}`}>{value}</span>
        </div>
    );
}
